/**
 * Customer Dashboard Page
 *
 * Main dashboard for customers showing:
 * - Action items (payments due, reviews pending, profile incomplete, trip prep)
 * - Upcoming trips
 * - Trip statistics
 * - Favorite guides
 *
 * Epic: MVP 1.16 - Customer Dashboard
 *
 * @version 1.0
 * @created 2025-11-24
 */

import React from 'react';
import { useLoaderData, useNavigate } from 'react-router-dom';
import {
  AlertCircle as _AlertCircle,
  AlertTriangle,
  Info,
  CheckCircle2,
  Calendar,
  DollarSign,
  Star as _Star,
  Users as _Users,
  Heart,
  MessageSquare
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import KPICard from '../../components/common/KPICard';
import Button from '../../components/common/Button';
import EmptyState from '../../components/common/EmptyState';
import { buildSrcSet } from '../../services/assetService';
import { formatCentsToDollars } from '../../utils/formHelpers';

export default function CustomerDashboard() {
  const { dashboard } = useLoaderData();
  const navigate = useNavigate();

  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };


  // Format date
  const formatDate = (dateString) => {
    if (!dateString) {return '';}
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Get urgency icon and color
  const getUrgencyStyle = (urgency) => {
    switch (urgency) {
      case 'critical':
        return {
          icon: _AlertCircle,
          bgColor: 'bg-red-100',
          textColor: 'text-red-800',
          iconColor: 'text-red-600',
          borderColor: 'border-red-200'
        };
      case 'high':
        return {
          icon: AlertTriangle,
          bgColor: 'bg-orange-100',
          textColor: 'text-orange-800',
          iconColor: 'text-orange-600',
          borderColor: 'border-orange-200'
        };
      case 'medium':
        return {
          icon: Info,
          bgColor: 'bg-yellow-100',
          textColor: 'text-yellow-800',
          iconColor: 'text-yellow-600',
          borderColor: 'border-yellow-200'
        };
      case 'low':
        return {
          icon: CheckCircle2,
          bgColor: 'bg-blue-100',
          textColor: 'text-blue-800',
          iconColor: 'text-blue-600',
          borderColor: 'border-blue-200'
        };
      default:
        return {
          icon: Info,
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-800',
          iconColor: 'text-gray-600',
          borderColor: 'border-gray-200'
        };
    }
  };

  // Action items summary
  const actionItemsSummary = dashboard?.action_items_summary || {};
  const actionItems = dashboard?.action_items || [];
  const upcomingTrips = dashboard?.upcoming_trips || [];
  const statistics = dashboard?.statistics || {};

  return (
    <DashboardLayout
      userRole="customer"
      activeNavId="dashboard"
      onNavigate={handleNavigate}
      user={{
        name: 'Customer User',
        email: 'customer@outdoorguides.com',
        avatar: null
      }}
    >
      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Dashboard</h1>
          <p className="text-sm text-gray-500">
            {actionItems.length > 0
              ? `You have ${actionItems.length} action item${actionItems.length > 1 ? 's' : ''} that need attention.`
              : 'Everything looks great! No action items at this time.'}
          </p>
        </div>

        {/* Clickable KPI Cards - US-1.21.4-01 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Upcoming Trips - Clickable */}
          <KPICard
            label="Upcoming Trips"
            value={statistics.upcoming_trips || 0}
            change={{
              value: 'Get ready!',
              trend: 'neutral'
            }}
            icon={Calendar}
            color="success"
            onClick={() => handleNavigate('trips', '/customer/trips')}
          />

          {/* Pending Reviews - Clickable */}
          <KPICard
            label="Pending Reviews"
            value={actionItemsSummary.by_type?.review_pending || 0}
            change={{
              value: actionItemsSummary.by_type?.review_pending > 0 ? 'Share your feedback' : 'All caught up',
              trend: actionItemsSummary.by_type?.review_pending > 0 ? 'down' : 'neutral'
            }}
            icon={_Star}
            color="warning"
            onClick={() => handleNavigate('reviews', '/customer/reviews')}
          />

          {/* Favorite Guides - Clickable */}
          <KPICard
            label="Favorite Guides"
            value={dashboard?.favorite_guides?.length || 0}
            change={{
              value: 'Saved for later',
              trend: 'neutral'
            }}
            icon={Heart}
            color="primary"
            onClick={() => handleNavigate('favorites', '/customer/favorites')}
          />

          {/* Messages - Clickable */}
          <KPICard
            label="Unread Messages"
            value={actionItemsSummary.by_type?.unread_message || 0}
            change={{
              value: actionItemsSummary.by_type?.unread_message > 0 ? 'Check messages' : 'No new messages',
              trend: actionItemsSummary.by_type?.unread_message > 0 ? 'up' : 'neutral'
            }}
            icon={MessageSquare}
            color="info"
            onClick={() => handleNavigate('messages', '/customer/messages')}
          />
        </div>

        {/* Action Items Section */}
        {actionItems.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Action Items</h2>
              <p className="mt-1 text-sm text-gray-500">
                Tasks that need your attention
              </p>
            </div>
            <div className="divide-y divide-gray-200">
              {actionItems.map((item) => {
                const style = getUrgencyStyle(item.urgency);
                const Icon = style.icon;

                return (
                  <div
                    key={item.id}
                    className={`p-6 hover:bg-gray-50 transition-colors border-l-4 ${style.borderColor}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded-lg ${style.bgColor}`}>
                        <Icon className={`w-5 h-5 ${style.iconColor}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h3 className="text-base font-medium text-gray-900">
                              {item.title}
                            </h3>
                            <p className="mt-1 text-sm text-gray-600">
                              {item.message}
                            </p>
                            {item.dueDate && (
                              <p className="mt-1 text-sm text-gray-500">
                                Due: {formatDate(item.dueDate)}
                                {item.daysUntil !== null && (
                                  <span className="ml-2 text-gray-400">
                                    ({item.daysUntil} day{item.daysUntil !== 1 ? 's' : ''})
                                  </span>
                                )}
                              </p>
                            )}
                          </div>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${style.bgColor} ${style.textColor}`}
                          >
                            {item.urgency}
                          </span>
                        </div>
                        {item.actionUrl && (
                          <div className="mt-3">
                            <button
                              onClick={() => navigate(item.actionUrl)}
                              className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
                            >
                              {item.actionLabel || 'View Details'}
                              <span className="ml-1">&rarr;</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Upcoming Trips Section */}
        {upcomingTrips.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Upcoming Trips</h2>
                <p className="mt-1 text-sm text-gray-500">
                  Your next adventures
                </p>
              </div>
              <button
                onClick={() => navigate('/customer/trips')}
                className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
              >
                View All
              </button>
            </div>
            <div className="divide-y divide-gray-200">
              {upcomingTrips.map((trip) => (
                <div key={trip.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex gap-4">
                    {/* Trip Image */}
                    <div className="flex-shrink-0">
                      <img
                        src={trip.service?.primaryUrl || trip.service?.images?.[0] || '/placeholder-trip.jpg'}
                        srcSet={buildSrcSet(trip.service?.variants) || undefined}
                        sizes={buildSrcSet(trip.service?.variants) ? '96px' : undefined}
                        alt={trip.service?.title}
                        className="w-24 h-24 rounded-lg object-cover"
                        loading="lazy"
                      />
                    </div>

                    {/* Trip Details */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-medium text-gray-900">
                        {trip.service?.title}
                      </h3>
                      <p className="mt-1 text-sm text-gray-600">
                        with {trip.guide?.full_name}
                        {trip.guide?.rating && (
                          <span className="ml-2 inline-flex items-center">
                            <_Star className="w-4 h-4 text-yellow-400 fill-current" />
                            <span className="ml-1">{trip.guide.rating.toFixed(1)}</span>
                          </span>
                        )}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                        <span className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {formatDate(trip.start_date)}
                        </span>
                        {trip.balance_due_cents > 0 && (
                          <span className="flex items-center text-orange-600 font-medium">
                            <DollarSign className="w-4 h-4 mr-1" />
                            Balance due: {formatCentsToDollars(trip.balance_due_cents)}
                          </span>
                        )}
                      </div>
                      <div className="mt-3">
                        <button
                          onClick={() => navigate(`/customer/trips/${trip.id}`)}
                          className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
                        >
                          View Details &rarr;
                        </button>
                      </div>
                    </div>

                    {/* Status Badge */}
                    <div className="flex-shrink-0">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                        trip.status === 'paid' ? 'bg-green-100 text-green-800' :
                        trip.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {trip.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {actionItems.length === 0 && upcomingTrips.length === 0 && (
          <EmptyState
            icon={CheckCircle2}
            iconClassName="w-16 h-16 text-emerald-500 mx-auto mb-4"
            title="All Caught Up!"
            description="You have no pending action items or upcoming trips."
            action={
              <Button onClick={() => navigate('/')}>
                Browse Experiences
              </Button>
            }
          />
        )}
      </div>
    </DashboardLayout>
  );
}
